<template>
  <button
    class="artifact-card"
    :class="`artifact-card--${variant}`"
    type="button"
    @click="handleCardClick"
  >
    <!-- 陪练任务：无文件图标，右侧为跳转箭头 -->
    <template v-if="variant === 'task'">
      <div class="artifact-card__info">
        <div class="artifact-card__title">AI 陪练任务</div>
        <div class="artifact-card__meta">基于分析内容，为您生成针对性的陪练任务</div>
      </div>
      <i aria-hidden="true" class="artifact-card__arrow icon-arrow-right-line t-iconfont"></i>
    </template>

    <!-- 文件投递物：图标 + 名称/时间，下载型额外带下载图标 -->
    <template v-else>
      <img alt="" aria-hidden="true" class="artifact-card__icon" :src="fileIcon" />
      <div class="artifact-card__info">
        <div class="artifact-card__title">{{ originalFilename }}</div>
        <div class="artifact-card__meta">{{ processedAt }}</div>
      </div>
      <!-- 下载按钮恒定占位、恒定可命中，整体透明度过渡避免布局和指针抖动 -->
      <span class="artifact-card__download" @click.stop="handleDownloadClick">
        <i
          aria-hidden="true"
          class="artifact-card__download-icon icon-download2-line t-iconfont"
        ></i>
      </span>
    </template>
  </button>
</template>

<script setup>
import { computed } from 'vue'
import utils from '@/utils'
import AppMessage from '@/components/message/index.js'

import htmlReportIcon from '@/assets/file-icon/html-report.svg'

import fileOtherIcon from '@/assets/file-icon/other.png'
import fileTextIcon from '@/assets/file-icon/text.png'
import filePdfIcon from '@/assets/file-icon/pdf.png'
import fileMdIcon from '@/assets/file-icon/md.png'
import fileWordIcon from '@/assets/file-icon/word.png'
import fileExcelIcon from '@/assets/file-icon/excel.png'
import fileImageIcon from '@/assets/file-icon/image.png'

defineOptions({
  name: 'ArtifactCard'
})

const props = defineProps({
  contentType: {
    type: String,
    default: ''
  },
  originalFilename: {
    type: String,
    default: ''
  },
  processedAt: {
    type: String,
    default: ''
  },
  isJson: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['click', 'download'])
const FILE_ICON_BY_EXTENSION = {
  pdf: filePdfIcon,
  doc: fileWordIcon,
  docx: fileWordIcon,
  xls: fileExcelIcon,
  xlsx: fileExcelIcon,
  csv: fileExcelIcon,
  md: fileMdIcon,
  markdown: fileMdIcon,
  txt: fileTextIcon,
  text: fileTextIcon,
  log: fileTextIcon,
  json: fileTextIcon,
  jsonl: fileTextIcon,
  yaml: fileTextIcon,
  yml: fileTextIcon,
  png: fileImageIcon,
  jpg: fileImageIcon,
  jpeg: fileImageIcon,
  gif: fileImageIcon,
  webp: fileImageIcon,
  bmp: fileImageIcon,
  svg: fileImageIcon
}

const propsFileExt = (filename) => {
  const ext = filename.split('.').pop()
  return ext ? ext.toLowerCase() : ''
}

// html 报告用专属图标，其余文件类型统一用 word 图标。
const fileIcon = computed(() => {
  return props.contentType === 'text/html'
    ? htmlReportIcon
    : FILE_ICON_BY_EXTENSION[propsFileExt(props.originalFilename)] || fileOtherIcon
})

// 结构化（JSON）投递物按陪练任务卡片展示；md/html 走预览；其余走下载。
const variant = computed(() => {
  if (props.isJson) return 'task'
  return 'preview'
})

// 下载型卡片：整卡点击不响应，仅点击下载图标才触发下载，由父级按现有方式取链接再触发下载。
const handleCardClick = () => {
  if (utils.isMobile() && variant.value === 'task') {
    AppMessage.warning('移动端暂不支持，请在网页端中操作')
    return
  }
  emit('click')
}

const handleDownloadClick = () => {
  emit('download')
}
</script>

<style lang="scss" scoped>
.artifact-card {
  display: flex;
  align-items: center;
  width: 350px;
  max-width: 100%;
  min-height: 64px;
  padding: 12px 16px;
  border: 1px solid #e5e8f5;
  border-radius: 16px;
  box-sizing: border-box;
  background: #ffffff;
  color: #242832;
  font-family: 'PingFang SC', sans-serif;
  cursor: pointer;
  text-align: left;
  transition:
    border-color 0.2s,
    background-color 0.2s;

  // 仅鼠标设备启用 hover，避免触屏首次点击只显示下载按钮。
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: #3370ff;
      background: #f7f9ff;

      .artifact-card__download {
        opacity: 1;
      }
    }
  }

  &--preview {
    width: 351px;
    min-height: 66px;
    // Figma 描边位于外框内侧，计入 border 后保持 16px / 12px 内容起点。
    padding: 11px 15px;

    &:active,
    &:focus-visible {
      border-color: #7a52f4;
      background: #ffffff;

      .artifact-card__download {
        opacity: 1;
      }
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        border-color: #7a52f4;
        background: #ffffff;

        .artifact-card__download {
          opacity: 1;
        }
      }
    }
  }

  &__icon {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    margin-right: 12px;
    object-fit: contain;
  }

  &__info {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
  }

  &__title {
    overflow: hidden;
    color: #242832;
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__meta {
    margin-top: 2px;
    overflow: hidden;
    color: #858e9f;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // 触屏默认可见；鼠标设备隐藏时仍保留尺寸，避免悬浮时标题宽度变化。
  &__download {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    margin-left: 12px;
    border: 1px solid #e5e8f5;
    border-radius: 8px;
    box-sizing: border-box;
    background: #ffffff;
    cursor: pointer;
    opacity: 1;
    transition:
      opacity 0.2s,
      background-color 0.2s;

    @media (hover: hover) and (pointer: fine) {
      opacity: 0;

      &:hover {
        background: #f5f7fb;
      }
    }

    &:active {
      background: #eeeeee;
    }
  }

  &__download-icon {
    color: #646d7d;
    font-size: 16px;
    line-height: 16px;
  }

  // 陪练任务卡片：样式与 AiPracticePartnerCard 保持一致。
  &--task {
    justify-content: space-between;
    border-color: #e5e6eb;

    .artifact-card__title {
      color: #323233;
      font-weight: 600;
      line-height: 20px;
    }

    .artifact-card__meta {
      margin-top: 4px;
      color: #969799;
      line-height: 16px;
    }
  }

  &--task .artifact-card__arrow {
    flex-shrink: 0;
    color: #646566;
    font-size: 16px;
  }

  // 下载型卡片保持中性态，不与预览型共用 hover 高亮；卡片本身不可点，指针不变手型
  // （下载图标容器自带 cursor: pointer）。
  &--download {
    cursor: default;
  }

  @media (hover: hover) and (pointer: fine) {
    &--download:hover {
      border-color: #e5e8f5;
      background: #ffffff;
    }
  }
}
</style>
